import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  KeyRound,
  HardDrive,
  CheckCircle2,
  PackageCheck,
  ScrollText,
  Package,
  Building2,
  UserPlus,
  FileText,
  CreditCard,
  Users,
  Shield,
  Globe2,
  Webhook,
  BarChart3,
  Activity,
  Cpu,
  ShieldCheck,
  Download,
  BookOpen,
  Bell,
  Settings,
  LogOut,
  X,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface SidebarProps {
  onLogout: () => void;
  user?: any;
  isOpen?: boolean;
  onClose?: () => void;
}

type UserRole = 'SUPER_ADMIN' | 'OWNER' | 'ADMIN' | 'MEMBER' | 'VIEWER';

interface NavItem {
  nameKey: string;
  path: string;
  icon: any;
  allowedRoles?: UserRole[];
}

export interface NavSection {
  titleKey: string;
  items: NavItem[];
}

const ADMIN: UserRole[] = ['OWNER', 'ADMIN'];
const STAFF: UserRole[] = ['OWNER', 'ADMIN', 'MEMBER'];

export const navigation: NavSection[] = [
  {
    titleKey: 'navigation.workspace',
    items: [
      { nameKey: 'navigation.dashboard', path: '/', icon: LayoutDashboard },
      { nameKey: 'navigation.licenses', path: '/licenses', icon: KeyRound },
      { nameKey: 'navigation.devices', path: '/devices', icon: HardDrive, allowedRoles: STAFF },
      { nameKey: 'navigation.activations', path: '/activations', icon: CheckCircle2, allowedRoles: STAFF },
      { nameKey: 'navigation.verificationPackages', path: '/verification', icon: PackageCheck, allowedRoles: STAFF },
      { nameKey: 'navigation.policies', path: '/policies', icon: ScrollText, allowedRoles: ADMIN },
    ],
  },
  {
    titleKey: 'navigation.commercial',
    items: [
      { nameKey: 'navigation.products', path: '/products', icon: Package },
      { nameKey: 'navigation.customers', path: '/customers', icon: Building2, allowedRoles: STAFF },
      { nameKey: 'navigation.partners', path: '/partners', icon: UserPlus, allowedRoles: ADMIN },
      { nameKey: 'navigation.purchaseOrders', path: '/purchase-orders', icon: FileText, allowedRoles: ADMIN },
      { nameKey: 'navigation.billing', path: '/billing', icon: CreditCard, allowedRoles: ADMIN },
    ],
  },
  {
    titleKey: 'navigation.accessIntegrations',
    items: [
      { nameKey: 'navigation.users', path: '/users', icon: Users, allowedRoles: ADMIN },
      { nameKey: 'navigation.rolesAndPermissions', path: '/roles', icon: Shield, allowedRoles: ADMIN },
      { nameKey: 'navigation.organizations', path: '/organizations', icon: Building2, allowedRoles: ADMIN },
      { nameKey: 'navigation.domains', path: '/domains', icon: Globe2, allowedRoles: ADMIN },
      { nameKey: 'navigation.apiKeys', path: '/api-keys', icon: KeyRound, allowedRoles: ADMIN },
      { nameKey: 'navigation.webhooks', path: '/webhooks', icon: Webhook, allowedRoles: ADMIN },
    ],
  },
  {
    titleKey: 'navigation.monitoringSecurity',
    items: [
      { nameKey: 'navigation.analytics', path: '/analytics', icon: BarChart3, allowedRoles: STAFF },
      { nameKey: 'navigation.activityLogs', path: '/activity-logs', icon: Activity, allowedRoles: STAFF },
      { nameKey: 'navigation.systemLogs', path: '/system-logs', icon: ScrollText, allowedRoles: ADMIN },
      { nameKey: 'navigation.systemSignatures', path: '/system-signatures', icon: Cpu, allowedRoles: ADMIN },
      { nameKey: 'navigation.mfa', path: '/mfa', icon: ShieldCheck },
    ],
  },
  {
    titleKey: 'navigation.resourcesAccount',
    items: [
      { nameKey: 'navigation.downloads', path: '/downloads', icon: Download },
      { nameKey: 'navigation.sdkDocs', path: '/sdk-docs', icon: BookOpen },
      { nameKey: 'navigation.notifications', path: '/notifications', icon: Bell },
      { nameKey: 'navigation.settings', path: '/settings', icon: Settings },
    ],
  },
];

function Sidebar({ onLogout, user, isOpen = false, onClose }: SidebarProps) {
  const location = useLocation();
  const { t } = useTranslation();

  // Desktop-only collapse to an icon rail (persisted). Mobile always uses the drawer.
  const [collapsed, setCollapsed] = useState<boolean>(
    () => localStorage.getItem('sidebar-collapsed') === 'true'
  );
  const toggleCollapsed = () =>
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('sidebar-collapsed', String(next));
      return next;
    });

  const hasAccess = (item: NavItem): boolean => {
    if (!item.allowedRoles || item.allowedRoles.length === 0) {
      return true;
    }
    if (!user || !user.role) {
      return false;
    }
    if (user.role === 'SUPER_ADMIN') {
      return true;
    }
    return item.allowedRoles.includes(user.role);
  };

  const filteredNavigation = navigation
    .map((section) => ({
      ...section,
      items: section.items.filter(hasAccess),
    }))
    .filter((section) => section.items.length > 0);

  const roleLabel = user?.role === 'SUPER_ADMIN' ? 'Platform workspace' : user?.role?.replace('_', ' ');

  return (
    <>
      {isOpen && <button className="sidebar-scrim" aria-label="Close navigation" onClick={onClose} />}
      <aside
        className={cn('app-sidebar', collapsed && 'is-collapsed', isOpen && 'is-open')}
        aria-label="Workspace navigation"
      >
        <div className="sidebar-brand od-row">
          <Link to="/" className="brand-lockup od-row od-fill" aria-label="AirGap Suite overview" onClick={onClose}>
            <span className="brand-symbol">
              <Shield className="od-icon" />
            </span>
            <span className="sidebar-label">
              AirGap<span className="brand-suite"> Suite</span>
            </span>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            className="sidebar-close lg:hidden"
            onClick={onClose}
            aria-label="Close navigation"
          >
            <X className="od-icon" />
          </Button>
        </div>

        {user && (
          <div className="workspace-label od-row">
            <Building2 className="od-icon" />
            <div className="od-field sidebar-label">
              <strong>{user.organization?.name || user.organizationName || 'Workspace'}</strong>
              <span>{roleLabel}</span>
            </div>
          </div>
        )}

        <nav className="sidebar-navigation">
          {filteredNavigation.map((section) => (
            <div key={section.titleKey} className="nav-group">
              <p className="nav-group-label sidebar-label">{t(section.titleKey)}</p>
              {section.items.map((item) => {
                const Icon = item.icon;
                const isActive =
                  location.pathname === item.path ||
                  (item.path === '/' && location.pathname === '/dashboard');
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={onClose}
                    title={collapsed ? t(item.nameKey) : undefined}
                    aria-label={t(item.nameKey)}
                    aria-current={isActive ? 'page' : undefined}
                    className={cn('nav-link od-row', isActive && 'active')}
                  >
                    <Icon className="od-icon" />
                    <span className="sidebar-label">{t(item.nameKey)}</span>
                    {isActive && <span className="nav-active-dot sidebar-label" />}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {user && (
          <footer className="sidebar-footer">
            <div className="od-row account-row">
              <div className="account-avatar">{(user.name || user.email || '?').charAt(0).toUpperCase()}</div>
              <div className="od-field od-fill sidebar-label">
                <strong className="od-truncate">{user.name || 'User'}</strong>
                <span className="od-truncate">{user.email}</span>
              </div>
            </div>
            <div className="od-row">
              <Button
                onClick={onLogout}
                variant="ghost"
                className="sidebar-signout od-fill"
                title={collapsed ? t('common.logout') : undefined}
              >
                <LogOut className="od-icon" />
                <span className="sidebar-label">{t('common.logout')}</span>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="sidebar-collapse"
                onClick={toggleCollapsed}
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {collapsed ? <PanelLeftOpen className="od-icon" /> : <PanelLeftClose className="od-icon" />}
              </Button>
            </div>
          </footer>
        )}
      </aside>
    </>
  );
}

export default Sidebar;
